import { FileText } from "lucide-react";
import RelatoriosPage from "./RelatoriosPage";
import "../styles/mobile-app.css";
import { useEffect, useMemo, useState } from "react";
import {
  Bell,
  CalendarDays,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Plus,
  Settings,
  Tags,
  Target,
  TrendingUp,
  Crown,
  WalletCards,
  CreditCard,
  User
} from "lucide-react";

import Dashboard from "../sections/Dashboard";
import Movimentacoes from "../sections/Movimentacoes";
import Categorias from "../sections/Categorias";
import Configuracoes from "../sections/Configuracoes";
import Metas from "../sections/Metas";
import Investimentos from "../sections/Investimentos";
import PlusPage from "../sections/Plus";
import QuickEntryModal from "../components/QuickEntryModal";
import DividasPage from "./DividasPage";

import {
  ouvirCategorias,
  ouvirConfiguracao,
  ouvirMovimentacoesMes,
  ouvirCofrinhos,
  ouvirInvestimentos,
  salvarInvestimento as salvarInvestimentoService,
  excluirInvestimento as excluirInvestimentoService,
} from "../services/financeService";

import { sairUsuario } from "../services/authService";
import { mesAtualChave } from "../utils/money";

const menu = [
  { id: "dashboard", label: "Início", icon: LayoutDashboard, premium: false },
  { id: "movimentacoes", label: "Movimentos", icon: WalletCards, premium: false },
  { id: "categorias", label: "Categorias", icon: Tags, premium: false },
  { id: "relatorios", label: "Relatórios", icon: FileText, premium: true },
  { id: "metas", label: "Metas", icon: Target, premium: true },
  { id: "dividas", label: "Dívidas", icon: CreditCard, premium: true },
  { id: "investimentos", label: "Investir", icon: TrendingUp, premium: true },
  { id: "plus", label: "Plus", icon: Crown, premium: false },
  { id: "config", label: "Ajustes", icon: Settings, premium: false },
];

const mobileMenu = [
  { id: "dashboard", label: "Início", icon: LayoutDashboard, premium: false },
  { id: "movimentacoes", label: "Movimentos", icon: WalletCards, premium: false },
  { id: "add", label: "", icon: Plus, premium: false },
  { id: "dividas", label: "Dívidas", icon: CreditCard, premium: true },
  { id: "mais", label: "Mais", icon: Settings, premium: false },
];

function InvexaLogo() {
  return (
    <img
      className="invexa-logo-img"
      src="/logo-invexa.png"
      alt="Invexa Finance"
    />
  );
}

function nomeUsuario(usuario, config) {
  const nome =
    usuario?.displayName ||
    usuario?.nome ||
    config?.nomeNegocio ||
    "Gustavo";

  return String(nome).split(" ")[0];
}

function plusAtivo(billing) {
  if (!billing) return false;

  const agora = Date.now();

  const accessExpiresAt =
    billing?.accessExpiresAt?.toDate?.()?.getTime?.() || 0;

  return (
    billing?.status === "active" &&
    billing?.assinaturaAtiva === true &&
    billing?.paymentApproved === true &&
    billing?.plano === "plus" &&
    accessExpiresAt > agora
  );
}

function isAbaPremium(aba) {
  return ["relatorios", "metas", "dividas", "investimentos"].includes(aba);
}

export default function MainApp({ usuario, billing, liberado }) {
  const [aba, setAba] = useState("dashboard");
  const [modalAberto, setModalAberto] = useState(false);
  const [menuMobileAberto, setMenuMobileAberto] = useState(false);
  const [notificacoesAberto, setNotificacoesAberto] = useState(false);
  const [perfilAberto, setPerfilAberto] = useState(false);
  const [mesSelecionado, setMesSelecionado] = useState(mesAtualChave());
  const [config, setConfig] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [cofrinhos, setCofrinhos] = useState([]);
  const [investimentos, setInvestimentos] = useState([]);

  const isPlus = plusAtivo(billing);
  const temAcessoPremium = isPlus || liberado === true;

  // Notificações reais entram aqui futuramente.
  // Enquanto estiver vazio, a bolinha verde não aparece.
  const notificacoes = useMemo(() => [], []);
  const temNotificacoes = notificacoes.length > 0;

  useEffect(() => {
    if (!usuario?.uid) return;

    const unsubConfig = ouvirConfiguracao(usuario.uid, setConfig);
    const unsubCategorias = ouvirCategorias(usuario.uid, setCategorias);
    const unsubCofrinhos = ouvirCofrinhos(usuario.uid, setCofrinhos);

    return () => {
      unsubConfig();
      unsubCategorias();
      unsubCofrinhos();
    };
  }, [usuario?.uid]);

  useEffect(() => {
    if (!usuario?.uid) return;

    const unsub = ouvirMovimentacoesMes(
      usuario.uid,
      mesSelecionado,
      setMovimentacoes
    );

    return () => unsub();
  }, [usuario?.uid, mesSelecionado]);

  useEffect(() => {
    if (!usuario?.uid) return;

    const unsubInvestimentos = ouvirInvestimentos(usuario.uid, setInvestimentos);

    return () => unsubInvestimentos();
  }, [usuario?.uid]);

  const resumo = useMemo(() => {
    const entradas = movimentacoes
      .filter((m) => m.tipo === "receita")
      .reduce((acc, m) => acc + Number(m.valor || 0), 0);

    const saidas = movimentacoes
      .filter((m) => m.tipo === "despesa")
      .reduce((acc, m) => acc + Number(m.valor || 0), 0);

    return {
      entradas,
      saidas,
      saldo: entradas - saidas,
    };
  }, [movimentacoes]);

  function abrirAba(id) {
    setAba(id);
  }

  function renderConteudo() {
    if (isAbaPremium(aba) && !temAcessoPremium) {
      return <PlusPage billing={billing} />;
    }

    if (aba === "relatorios") {
  return <RelatoriosPage usuario={usuario} />;
}

    if (aba === "movimentacoes") {
      return (
        <Movimentacoes
          uid={usuario.uid}
          movimentacoes={movimentacoes}
          categorias={categorias}
        />
      );
    }

    if (aba === "categorias") {
      return <Categorias uid={usuario.uid} categorias={categorias} />;
    }

    if (aba === "metas") {
      return (
        <Metas
          uid={usuario.uid}
          resumo={resumo}
          movimentacoes={movimentacoes}
          cofrinhos={cofrinhos}
        />
      );
    }

    if (aba === "dividas") {
      return <DividasPage uid={usuario.uid} />;
    }

    if (aba === "investimentos") {
      return (
        <Investimentos
          investimentos={investimentos}
          salvarInvestimento={(dados) => salvarInvestimentoService(usuario.uid, dados)}
          excluirInvestimento={(id) => excluirInvestimentoService(usuario.uid, id)}
        />
      );
    }

    if (aba === "plus") {
      return <PlusPage billing={billing} />;
    }

    if (aba === "config") {
      return (
        <Configuracoes uid={usuario.uid} config={config} billing={billing} />
      );
    }

    return (
      <Dashboard
        usuario={usuario}
        resumo={resumo}
        movimentacoes={movimentacoes}
        mesSelecionado={mesSelecionado}
        cofrinhos={cofrinhos}
      />
    );
  }

  const tituloAtual = menu.find((m) => m.id === aba)?.label || "Início";
  const primeiroNome = nomeUsuario(usuario, config);

  return (
    <div className="app-shell invexa-final-shell">
      <aside className="sidebar final-sidebar">
        <div className="sidebar-brand premium-brand">
          <InvexaLogo />
        </div>

        <nav>
          {menu.map((item) => {
            const Icon = item.icon;
            const bloqueada = item.premium && !temAcessoPremium;

            return (
              <button
                key={item.id}
                className={aba === item.id ? "nav-item active" : "nav-item"}
                onClick={() => abrirAba(item.id)}
                type="button"
                title={bloqueada ? "Recurso Plus" : item.label}
              >
                <Icon size={18} />
                {item.label}
                {bloqueada && <Crown size={14} style={{ marginLeft: "auto" }} />}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-pro-card">
          <span>{temAcessoPremium ? "Invexa Plus ativo" : "Invexa Plus"}</span>
          <p>
            {temAcessoPremium
              ? "Você tem acesso aos recursos avançados."
              : "Recursos avançados para evoluir seu controle financeiro."}
          </p>
          <button type="button" onClick={() => setAba("plus")}>
            {temAcessoPremium ? "Ver plano" : "Conhecer plano"}
          </button>
        </div>

        <button className="logout-btn" onClick={sairUsuario} type="button">
          <LogOut size={18} />
          Sair
        </button>
      </aside>

      <main className="content-area final-content-area">
        <div className="mobile-brand">
          <InvexaLogo />
        </div>

        <header className="topbar final-topbar">
          <div className="topbar-title-block">
            <span className="topbar-greeting">
              Olá, {primeiroNome} <span className="wave">👋</span>
            </span>

            <h1>{tituloAtual}</h1>

            <p>Resumo financeiro do mês selecionado.</p>
          </div>

          <div className="topbar-actions final-actions">
            <label className="month-picker premium-month-picker">
              <CalendarDays size={17} />

              <input
                type="month"
                value={mesSelecionado}
                onChange={(e) => setMesSelecionado(e.target.value)}
              />

              <ChevronDown size={16} className="month-chevron" />
            </label>

            <button
              className="primary-btn compact desktop-only premium-quick-btn"
              onClick={() => setModalAberto(true)}
              type="button"
            >
              <Plus size={18} />
              Entrada rápida
            </button>

            <div className="desktop-dropdown-wrap desktop-only">
              <button
                className="topbar-round-btn"
                type="button"
                aria-label="Notificações"
                onClick={() => {
                  setNotificacoesAberto((atual) => !atual);
                  setPerfilAberto(false);
                }}
              >
                <Bell size={18} />
                {temNotificacoes && <i />}
              </button>

              {notificacoesAberto && (
                <div className="desktop-popover notifications-popover">
                  <div className="desktop-popover-header">
                    <div>
                      <strong>Notificações</strong>
                      <span>Invexa Finance</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setNotificacoesAberto(false)}
                    >
                      Fechar
                    </button>
                  </div>

                  {temNotificacoes ? (
                    notificacoes.map((notificacao) => (
                      <div className="notification-item" key={notificacao.id}>
                        <div className={`notification-icon ${notificacao.cor || "blue"}`}>
                          <Bell size={17} />
                        </div>

                        <div>
                          <strong>{notificacao.titulo}</strong>
                          <span>{notificacao.texto}</span>
                        </div>

                        <small>{notificacao.tempo || "Agora"}</small>
                      </div>
                    ))
                  ) : (
                    <div className="notification-item">
                      <div className="notification-icon green">
                        <Bell size={17} />
                      </div>

                      <div>
                        <strong>Tudo em ordem!</strong>
                        <span>Não há novas mensagens no momento.</span>
                      </div>

                      <small>Agora</small>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="desktop-dropdown-wrap desktop-only">
              <button
                className="topbar-profile-btn"
                type="button"
                aria-label="Perfil"
                onClick={() => {
                  setPerfilAberto((atual) => !atual);
                  setNotificacoesAberto(false);
                }}
              >
                <User size={18} />
                <ChevronDown size={15} />
              </button>

              {perfilAberto && (
                <div className="desktop-popover profile-popover">
                  <div className="desktop-profile-head">
                    <div className="desktop-avatar">
                      {primeiroNome.slice(0, 1).toUpperCase()}
                    </div>

                    <div>
                      <strong>{primeiroNome}</strong>
                      <span>Invexa Finance</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setAba("config");
                      setPerfilAberto(false);
                    }}
                  >
                    <Settings size={17} />
                    Ajustes
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setAba("plus");
                      setPerfilAberto(false);
                    }}
                  >
                    <Crown size={17} />
                    Invexa Plus
                  </button>

                  <button
                    type="button"
                    className="danger"
                    onClick={sairUsuario}
                  >
                    <LogOut size={17} />
                    Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {renderConteudo()}
      </main>
<nav className="mobile-bottom-nav">
        {mobileMenu.map((item) => {
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              className={
                item.id === "add"
                  ? "mobile-nav-item mobile-nav-add"
                  : aba === item.id
                  ? "mobile-nav-item active"
                  : "mobile-nav-item"
              }
              onClick={() => {
                if (item.id === "add") {
                  setModalAberto(true);
                  return;
                }

                if (item.id === "mais") {
                  setMenuMobileAberto(true);
                  return;
                }

                abrirAba(item.id);
              }}
              type="button"
            >
              <Icon size={20} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {menuMobileAberto && (
        <div
          className="mobile-more-backdrop"
          onClick={() => setMenuMobileAberto(false)}
        >
          <div
            className="mobile-more-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-more-handle" />

            <button
              className="mobile-more-item"
              onClick={() => {
                setAba("metas");
                setMenuMobileAberto(false);
              }}
              type="button"
            >
              <Target size={18} />
              <span>Metas e cofrinhos</span>
              {!temAcessoPremium && <Crown size={15} />}
            </button>

            <button
              className="mobile-more-item"
              onClick={() => {
                setAba("categorias");
                setMenuMobileAberto(false);
              }}
              type="button"
            >
              <Tags size={18} />
              <span>Categorias</span>
            </button>

            <button
              className="mobile-more-item"
              onClick={() => {
                setAba("relatorios");
                setMenuMobileAberto(false);
              }}
              type="button"
            >
              <FileText size={18} />
              <span>Relatórios</span>
              {!temAcessoPremium && <Crown size={15} />}
            </button>

            <button
              className="mobile-more-item"
              onClick={() => {
                setAba("investimentos");
                setMenuMobileAberto(false);
              }}
              type="button"
            >
              <TrendingUp size={18} />
              <span>Investir</span>
              {!temAcessoPremium && <Crown size={15} />}
            </button>

            <button
              className="mobile-more-item"
              onClick={() => {
                setAba("plus");
                setMenuMobileAberto(false);
              }}
              type="button"
            >
              <Crown size={18} />
              <span>Invexa Plus</span>
            </button>

            <button
              className="mobile-more-item"
              onClick={() => {
                setAba("config");
                setMenuMobileAberto(false);
              }}
              type="button"
            >
              <Settings size={18} />
              <span>Ajustes</span>
            </button>

            <button
              className="mobile-more-item danger"
              onClick={sairUsuario}
              type="button"
            >
              <LogOut size={18} />
              <span>Sair</span>
            </button>
          </div>
        </div>
      )}

      {modalAberto && (
        <QuickEntryModal
          uid={usuario.uid}
          categorias={categorias}
          onClose={() => setModalAberto(false)}
        />
      )}
    </div>
  );
}

